import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, Copy } from 'lucide-react';
import axiosInstance from 'shared/services/api/axiosInstance';
import { useTeam } from 'shared/context/teamContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import {
  settingsInputClass,
  settingsTextareaClass,
  settingsSolidButtonClass,
  settingsIconButtonClass,
} from 'features/settings/components/settingsTheme';
import { activityNeutralButtonClass } from 'features/activity/styles/dataTableStyles';
import { Section, Row } from 'features/settings/components/SettingsRows';
import teamService from 'shared/services/teamService';

/**
 * Settings → Project → General.
 *
 * Owner-only surface for the workspace itself: its name, its shared context,
 * and deleting it.
 *
 * Gate note: we branch on `selectedTeam.isOwner`, not the role matrix. The
 * backend's updateTeam/deleteTeam guards are `isCreator || isFirstOwner`, and
 * `isOwner` (GET /team/:teamId) is computed the same positional way — so it is
 * the only client signal that agrees with the server. A user can hold
 * userRole === 'owner' without being users[0], and would then see controls that
 * 403. Non-owners see the controls disabled rather than hidden, so the page
 * doesn't silently differ per role.
 */


const ProjectSettingsPanel = () => {
  const { selectedTeam, setSelectedTeam } = useTeam();
  const { confirm: appConfirm } = useAppDialog();
  const navigate = useNavigate();
  const location = useLocation();

  const [teamName, setTeamName] = useState('');
  const [projectContext, setProjectContext] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);

  const teamId = selectedTeam?.id ?? selectedTeam?._id ?? null;
  const isOwner = Boolean(selectedTeam?.isOwner);
  const siteId = selectedTeam?.siteId || null;

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  const handleCopySiteId = async () => {
    if (!siteId) return;
    try {
      await navigator.clipboard.writeText(siteId);
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1800);
    } catch {
      setSaveError('Could not copy to clipboard.');
    }
  };

  const savedName = selectedTeam?.name || '';
  const savedContext = useMemo(
    () => (typeof selectedTeam?.context === 'string' ? selectedTeam.context : ''),
    [selectedTeam?.context]
  );

  useEffect(() => {
    setTeamName(savedName);
  }, [savedName]);

  useEffect(() => {
    setProjectContext(savedContext);
  }, [savedContext]);

  const trimmedName = teamName.trim();
  const nameChanged = trimmedName !== savedName.trim();
  const contextChanged = projectContext !== savedContext;
  const isDirty = nameChanged || contextChanged;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!teamId || !isOwner) return;

    // The team name is a URL segment — an empty one would produce a dead route.
    // The backend only skips falsy names, so " " would otherwise be written.
    if (!trimmedName) {
      setSaveError('Team name cannot be empty.');
      return;
    }

    setSaving(true);
    setSaveMessage('');
    setSaveError('');

    try {
      const payload = {};
      if (nameChanged) payload.name = trimmedName;
      if (contextChanged) payload.context = projectContext;

      const response = await axiosInstance.put(`/team/${teamId}`, payload);
      // The cached team row is now stale — the next reader must go to the
      // server rather than be served a pre-change name/role for the session.
      teamService.invalidate(teamId, 'team');
      const updated = response?.data?.team || {};
      const nextName = typeof updated.name === 'string' ? updated.name : trimmedName;
      const nextContext =
        typeof updated.context === 'string' ? updated.context : projectContext;

      if (selectedTeam) {
        setSelectedTeam({ ...selectedTeam, name: nextName, context: nextContext });
      }
      setProjectContext(nextContext);
      setTeamName(nextName);

      if (nameChanged) {
        // The name is the first URL segment, so a rename strands the user on a
        // dead path. Bust the switcher's cached team list, then move to the new
        // URL keeping the current tail (/settings/project) rather than bouncing
        // them to chat.
        try {
          sessionStorage.removeItem('userTeams');
        } catch {
          /* sessionStorage blocked — the switcher will refetch eventually. */
        }
        const tail = location.pathname.replace(/^\/[^/]+/, '') || '/settings/project';
        navigate(`/${encodeURIComponent(nextName)}${tail}`, { replace: true });
      }

      setSaveMessage('Saved');
      setTimeout(() => setSaveMessage(''), 2500);
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Failed to save workspace settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!teamId || !savedName || !isOwner) return;
    const confirmed = await appConfirm(
      `Delete team "${savedName}"? This permanently removes the team and its data for all members. This cannot be undone.`,
      { title: 'Delete team', confirmLabel: 'Delete', danger: true }
    );
    if (!confirmed) return;

    setDeleteLoading(true);
    setSaveError('');
    try {
      await axiosInstance.delete(`/team/${teamId}`);
      const res = await axiosInstance.get('/team/details');
      const teamList = res.data?.team || [];
      try {
        sessionStorage.removeItem('userTeams');
      } catch {
        /* non-fatal */
      }
      if (teamList.length > 0) {
        const next = teamList[0];
        setSelectedTeam(next);
        navigate(`/${encodeURIComponent(next.name)}/settings/project`, { replace: true });
      } else {
        setSelectedTeam(null);
        navigate('/login', { replace: true });
      }
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Could not delete team');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!teamId) {
    return (
      <p className="py-10 text-center text-[12px] text-[color:var(--text-muted)]" style={{ fontFamily: 'Inter, sans-serif' }}>
        Select a project.
      </p>
    );
  }

  const disabledCls = 'disabled:cursor-not-allowed disabled:opacity-60';

  return (
    <section className="w-full min-w-0 max-w-3xl pb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
      <form onSubmit={handleSave}>
        <Section title="General" className="mb-4">
          <Row label={<label htmlFor="team-name">Team name</label>}>
            <input
              id="team-name"
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              disabled={!isOwner || saving}
              className={`${settingsInputClass} ${disabledCls}`}
              placeholder="Team name"
            />
          </Row>
          {siteId && (
            <Row label="Site ID">
              <code className="min-w-0 truncate font-mono text-[12px] tabular-nums text-[color:var(--text-primary)]" title={siteId}>
                {siteId}
              </code>
              <button
                type="button"
                onClick={handleCopySiteId}
                aria-label="Copy site ID"
                title={copied ? 'Copied' : 'Copy'}
                className={settingsIconButtonClass}
              >
                {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
              </button>
            </Row>
          )}
          <Row label={<label htmlFor="team-context">Team context</label>} align="start">
            <textarea
              id="team-context"
              value={projectContext}
              onChange={(e) => setProjectContext(e.target.value)}
              disabled={!isOwner || saving}
              className={`${settingsTextareaClass} min-h-[100px] ${disabledCls}`}
              placeholder="Shared with everyone in this team"
              rows={5}
            />
          </Row>
        </Section>

        {!isOwner && (
          <p className="mb-2 text-[12px] text-[color:var(--text-muted)]">Only the owner can change these.</p>
        )}
        {saveMessage && <p role="status" className="mb-2 text-[12px] text-success">{saveMessage}</p>}
        {saveError && <p role="alert" className="mb-2 text-[12px] text-danger">{saveError}</p>}

        {isOwner && isDirty && (
          <div className="mb-4">
            <button type="submit" disabled={saving} className={settingsSolidButtonClass}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </form>

      <Section title="Delete team" className="mt-8">
        <Row label="Removes the team and its data for all members.">
          <button
            type="button"
            onClick={handleDeleteTeam}
            disabled={!isOwner || deleteLoading}
            title={!isOwner ? 'Only the owner can delete the team.' : undefined}
            className={`${activityNeutralButtonClass} !text-[color:var(--feedback-error)]`}
          >
            {deleteLoading ? 'Deleting…' : 'Delete team'}
          </button>
        </Row>
      </Section>
    </section>
  );
};

export default ProjectSettingsPanel;
